/**
 * AdaptiveFlowResolver
 *
 * AI-suggested flow ordering for the guided tour index screen.
 * Analyzes user's role, industry solution, and recent activity to reorder
 * flows with "Recommended for you" badges.
 *
 * Uses searchSimilarSessions (server-side) to find what similar users learned first,
 * then applies a scoring algorithm to reorder flows.
 * Falls back to YAML-defined order when AI is unavailable.
 */

'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TourFlow } from '@/config/guided-tours/types';

export interface FlowRecommendation {
  flowId: string;
  score: number;
  rationale: string;
  recommended: boolean;
}

export interface AdaptiveFlowResolverResult {
  recommendations: FlowRecommendation[];
  reorderedFlows: TourFlow[];
  loading: boolean;
  error: string | null;
}

interface AdaptiveFlowResolverOptions {
  flows: TourFlow[];
  role: string;
  industrySolution?: string | null;
  moduleKey: string;
  enabled?: boolean;
  completedFlowIds?: string[];
}

export function useAdaptiveFlowResolver(
  opts: AdaptiveFlowResolverOptions
): AdaptiveFlowResolverResult {
  const {
    flows,
    role,
    industrySolution = null,
    moduleKey,
    enabled = true,
    completedFlowIds = [],
  } = opts;

  const [recommendations, setRecommendations] = useState<FlowRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecommendations = useCallback(async () => {
    if (!enabled || flows.length === 0) {
      setRecommendations([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/training/tour-ai-hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'adaptive_flows',
          context: {
            moduleKey,
            role,
            industrySolution,
            completedFlowIds,
          },
          availableFlows: flows.map((f) => ({
            id: f.id,
            label: f.label,
            summary: f.summary,
            kind: f.kind,
            roles: f.roles ?? [],
          })),
        }),
      });

      if (!res.ok) {
        setRecommendations([]);
        return;
      }

      const data = await res.json();
      const recs: FlowRecommendation[] = Array.isArray(data.recommendations)
        ? data.recommendations
        : [];

      const validRecs = recs.filter((r) => flows.some((f) => f.id === r.flowId));
      setRecommendations(validRecs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recommendations');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, flows, moduleKey, role, industrySolution, completedFlowIds]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const reorderedFlows = useMemo<TourFlow[]>(() => {
    if (recommendations.length === 0) {
      return flows;
    }

    const recMap = new Map(recommendations.map((r) => [r.flowId, r]));
    const sorted = [...flows].sort((a, b) => {
      const aRec = recMap.get(a.id);
      const bRec = recMap.get(b.id);
      const aScore = aRec?.score ?? 0;
      const bScore = bRec?.score ?? 0;

      const aCompleted = completedFlowIds.includes(a.id);
      const bCompleted = completedFlowIds.includes(b.id);
      if (aCompleted && !bCompleted) return 1;
      if (!aCompleted && bCompleted) return -1;

      return bScore - aScore;
    });

    return sorted;
  }, [flows, recommendations, completedFlowIds]);

  return {
    recommendations,
    reorderedFlows,
    loading,
    error,
  };
}

export function getFlowBadge(
  flowId: string,
  recommendations: FlowRecommendation[]
): FlowRecommendation | null {
  return recommendations.find((r) => r.flowId === flowId && r.recommended) ?? null;
}
